import React, { useEffect, useState } from "react";
import {
  classifyIdentity,
  validateEmail,
  signUpEmailPassword,
  signInEmailPassword,
  getSessionUser,
  getUserRole,
} from "../../services/auth/unifiedAuth";
import { supabase, supabaseReady } from "../../lib/supabaseClient";
import { SocialAuthButtons } from "./SocialAuthButtons";

function translateAuthError(message: string): string {
  const errorMap: Record<string, string> = {
    'email rate limit exceeded': 'Limite de emails atingido. Aguarde alguns minutos e tente novamente.',
    'over_email_send_rate_limit': 'Limite de emails atingido. Aguarde alguns minutos e tente novamente.',
    'email address is invalid': 'Este email não é válido. Use um email real (ex: seunome@gmail.com).',
    'email_address_invalid': 'Este email não é válido. Use um email real (ex: seunome@gmail.com).',
    'user already registered': 'Este email já está cadastrado. Tente fazer login.',
    'invalid login credentials': 'Email ou senha incorretos.',
    'email not confirmed': 'Confirme seu email antes de fazer login.',
    'password should be at least': 'A senha deve ter no mínimo 8 caracteres.',
  };
  
  const lowerMessage = message.toLowerCase();
  for (const [key, value] of Object.entries(errorMap)) {
    if (lowerMessage.includes(key.toLowerCase())) {
      return value;
    }
  }
  return message;
}

type Props = { 
  redirectAfter?: string;
  initialMode?: "signup" | "login";
  hideToggle?: boolean;
  hideSocial?: boolean;
};

export const OnboardingForm: React.FC<Props> = ({
  redirectAfter = import.meta.env.VITE_POST_LOGIN_REDIRECT || "/userhome",
  initialMode = "signup",
  hideToggle = false,
  hideSocial = false,
}) => {
  const [mode, setMode] = useState<"signup" | "login">(initialMode);
  const [fullName, setFullName] = useState("");
  const [identity, setIdentity] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Função util para redirecionar
  function goRedirect(dest?: string) {
    const finalDest = (dest || redirectAfter || import.meta.env.VITE_POST_LOGIN_REDIRECT || "/userhome").trim();
    if (window.location.pathname !== finalDest) {
      if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
        console.log("[ONBOARDING] redirecting to", finalDest);
      }
      window.location.replace(finalDest);
    }
  }

  useEffect(() => {
    // Comentado para evitar AbortError (Lock broken) no Supabase Auth
    // O redirecionamento de usuários já logados será tratado por layouts superiores ou manualmente após login
    /*
    let mounted = true;
    (async () => {
      try {
        const user = await getSessionUser();
        if (mounted && user) {
          console.log("[AUTH] Session found on mount, redirecting...");
          goRedirect();
        }
      } catch (e) {
        console.error("[AUTH] Early session check failed:", e);
      }
    })();
    return () => { mounted = false; };
    */
  }, [redirectAfter]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return; // evita duplo clique

    setError(null);
    setSuccessMessage(null);

    if (!supabaseReady) {
      setError("Supabase não configurado.");
      return;
    }

    const kind = classifyIdentity(identity);

    if (kind === "invalid") {
      setError("Informe um e-mail válido.");
      return;
    }

    // Se usuário digitou telefone, pedir email
    if (kind === "phone") {
      setError("Por favor, use um e-mail para se cadastrar. Login por telefone não está disponível.");
      return;
    }

    // Validação extra para emails
    if (kind === "email") {
      const emailValidation = validateEmail(identity);
      if (!emailValidation.valid) {
        setError(emailValidation.message || "Email inválido.");
        return;
      }
    }

    // Validação de senha
    if (password.length < 8) {
      setError("Senha mínimo 8 caracteres.");
      return;
    }

    try {
      setLoading(true);
      if (mode === "signup") {
        const user = await signUpEmailPassword(identity, password, fullName || "—");
        if (user) {
          setSuccessMessage("Conta criada! Verifique seu email para confirmar o cadastro.");
        }
      } else {
        if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
          console.log("[AUTH] Attempting sign in for:", identity);
        }
        const user = await signInEmailPassword(identity, password);
        
        if (user) {
          if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
            console.log("[AUTH] User authenticated successfully:", user.id);
          }
          const role = await getUserRole(user.id);
          if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
            console.log("[AUTH] User role resolved:", role);
          }
          
          const isAdminPath = window.location.pathname.startsWith("/admin");

          if (role === "admin" && !isAdminPath) {
            if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
              console.warn("[AUTH] Admin trying to login via user portal.");
            }
            await supabase.auth.signOut();
            setError("Administradores devem usar o portal exclusivo em /admin");
            setLoading(false);
            return;
          }

          if (role !== "admin" && isAdminPath) {
            if (import.meta.env.MODE === "development" && import.meta.env.VITE_VERBOSE_LOGS === "true") {
              console.warn("[AUTH] Non-admin trying to login via admin portal. Role found:", role);
            }
            await supabase.auth.signOut();
            setError(role ? "Acesso restrito a administradores. Seu papel atual: " + role : "Acesso restrito a administradores. Verifique se a tabela 'user_roles' foi criada no SQL.");
            setLoading(false);
            return;
          }

          setSuccessMessage("Login realizado!");
          goRedirect();
        }
      }
    } catch (err: any) {
      console.error("[AUTH] Error during handleSubmit:", err);
      // Se for o erro de lock, dar uma mensagem amigável e sugerir tentar de novo
      if (err.message?.includes("Lock broken") || err.name === "AbortError") {
        setError("O navegador bloqueou a sincronização. Clique em 'Entrar' novamente em 2 segundos.");
        setTimeout(() => setError(null), 3000);
      } else {
        setError(translateAuthError(err.message));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {!hideSocial && (
        <>
          <SocialAuthButtons 
            loading={loading} 
            onError={(msg) => setError(msg)} 
            redirectPath={window.location.pathname.startsWith("/admin") ? "/admin" : redirectAfter} 
          />
          <div className="text-center text-xs text-muted-foreground">ou</div>
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="text-sm text-red-600">{error}</div>}
        {successMessage && <div className="text-sm text-green-600">{successMessage}</div>}

        {mode === "signup" && (
          <div className="space-y-1">
            <label className="text-sm font-medium">Nome completo</label>
            <input
              className="w-full rounded border px-3 py-2"
              placeholder="Seu nome"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              disabled={loading}
            />
          </div>
        )}

        <div className="space-y-1">
          <label className="text-sm font-medium">E-mail</label>
          <input
            type="email"
            className="w-full rounded border px-3 py-2"
            placeholder="email@exemplo.com"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            required
            disabled={loading}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Senha</label>
          <input
            type="password"
            className="w-full rounded border px-3 py-2"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !supabaseReady}
          className="w-full rounded bg-primary text-white py-2 font-medium disabled:opacity-60"
        >
          {loading
            ? mode === "signup"
              ? "Processando..."
              : "Entrando..."
            : mode === "signup"
              ? "Criar Conta"
              : "Entrar"}
        </button>
      </form>

      {!hideToggle && (
        <div className="text-sm text-center">
          {mode === "signup" ? (
            <>
              Já tem conta?{" "}
              <button
                type="button"
                onClick={() => setMode("login")}
                className="text-primary hover:underline"
                disabled={loading}
              >
                Entrar
              </button>
            </>
          ) : (
            <>
              Novo por aqui?{" "}
              <button
                type="button"
                onClick={() => setMode("signup")}
                className="text-primary hover:underline"
                disabled={loading}
              >
                Criar Conta
              </button>
            </>
          )}
        </div>
      )}

      {!supabaseReady && <div className="text-xs text-amber-600 text-center">Supabase não configurado.</div>}
    </div>
  );
};
